import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';

import Alert from '../Alert';
import Spinner from '../Spinner';
import useAlbumSlideshow from '../../../apps/pro/views/Album/AlbumSlideshow/hooks/useAlbumSlideshow';
import PhoneForm from './components/PhoneForm';
import PinVerifyForm from './components/PinVerifyForm';
import EmailPasswordForm from './components/EmailPasswordForm';
import WaldoSpeechBubble from '../WaldoSpeechBubble';
import { getErrorString } from '../../../util/normalize-errors';
import { loginSuccess } from '../../../actions/auth-actions';
import useSlideshowOnboarding from './hooks/useSlideshowOnboarding';
import SlideUploader from './components/SlideUploader';
import SlideUploadSuccess from './components/SlideUploadSuccess';

const propTypes = {
  albumId: PropTypes.string.isRequired,
  onRequestClose: PropTypes.func.isRequired,
};

const STEPS = {
  EMAIL: 'EMAIL',
  PHONE: 'PHONE',
  PIN_VERIFY: 'PIN_VERIFY',
  PHOTO_UPLOAD: 'PHOTO_UPLOAD',
  PHOTO_UPLOAD_SUCCESS: 'PHOTO_UPLOAD_SUCCESS',
};

const AlbumSlideshowOnboarding = ({ albumId, onRequestClose }) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState(STEPS.PHONE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const { slideshow } = useAlbumSlideshow({ albumId });
  const {
    onAccountLogin,
    onAccountLoginByPhone,
    onPhoneVerificationRequest,
  } = useSlideshowOnboarding();

  useEffect(() => {
    setStep(authenticated ? STEPS.PHOTO_UPLOAD : STEPS.PHONE);
  }, [authenticated]);

  const attemptPhoneVerificationRequest = async ({
    phone_number: phoneNumber,
  }) => {
    setLoading(true);
    setError('');

    try {
      await onPhoneVerificationRequest(phoneNumber);
      if (step !== STEPS.PIN_VERIFY) {
        setStep(STEPS.PIN_VERIFY);
      }
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const attemptEmailPasswordLogin = async ({ email, password }) => {
    setLoading(true);
    setError('');

    try {
      await onAccountLogin(email, password);
      dispatch(loginSuccess());
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const attemptPhoneLogin = async ({ phone_number: phoneNumber, code }) => {
    setLoading(true);
    setError('');

    try {
      await onAccountLoginByPhone(phoneNumber, code);
      dispatch(loginSuccess());
    } catch (err) {
      setError(getErrorString(err));
    } finally {
      setLoading(false);
    }
  };

  const onLogin = async values => {
    if (step === STEPS.EMAIL) {
      await attemptEmailPasswordLogin(values);
    }

    if (step === STEPS.PHONE) {
      await attemptPhoneVerificationRequest(values);
    }

    if (step === STEPS.PIN_VERIFY) {
      await attemptPhoneLogin(values);
    }
  };

  if (!slideshow) {
    return (
      <div className="w-100 h-100vh d-flex align-items-center justify-content-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="px-lg-2 pb-3">
      <WaldoSpeechBubble className="mb-4">
        <p className="px-4 text-bold">
          {step === STEPS.PHONE && (
            <Fragment>
              Add photos to <br />
              this slideshow!
            </Fragment>
          )}
          {step === STEPS.PIN_VERIFY && (
            <Fragment>A PIN code has been sent to your phone!</Fragment>
          )}
          {step === STEPS.PHOTO_UPLOAD && (
            <Fragment>Add your photos to the slideshow!</Fragment>
          )}
          {step === STEPS.PHOTO_UPLOAD_SUCCESS && (
            <Fragment>
              Your photos have been uploaded! What&apos;s next?
            </Fragment>
          )}
        </p>
      </WaldoSpeechBubble>
      {![STEPS.PHOTO_UPLOAD, STEPS.PHOTO_UPLOAD_SUCCESS].includes(step) && (
        <Formik
          onSubmit={onLogin}
          initialValues={{
            email: '',
            password: '',
            phone_number: '+1',
            code: '',
          }}
          validateOnBlur={false}
          validateOnChange={false}
          render={({ values, handleSubmit, handleChange, handleBlur }) => {
            return (
              <>
                {step === STEPS.EMAIL && (
                  <EmailPasswordForm
                    handleSubmit={handleSubmit}
                    loading={loading}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    values={values}
                    onLoginViaPhone={() => setStep(STEPS.PHONE)}
                  />
                )}
                {step === STEPS.PHONE && (
                  <PhoneForm
                    handleSubmit={handleSubmit}
                    loading={loading}
                    handleChange={handleChange}
                    values={values}
                    onLoginViaEmail={() => setStep(STEPS.EMAIL)}
                  />
                )}
                {step === STEPS.PIN_VERIFY && (
                  <PinVerifyForm
                    handleSubmit={handleSubmit}
                    loading={loading}
                    handleChange={handleChange}
                    values={values}
                    onPinCodeResend={attemptPhoneVerificationRequest}
                  />
                )}
                {error && (
                  <Alert type="danger" className="mt-2">
                    {error}
                  </Alert>
                )}
              </>
            );
          }}
        />
      )}
      {step === STEPS.PHOTO_UPLOAD && (
        <SlideUploader
          onUploadSuccess={() => setStep(STEPS.PHOTO_UPLOAD_SUCCESS)}
          albumId={albumId}
        />
      )}
      {step === STEPS.PHOTO_UPLOAD_SUCCESS && (
        <SlideUploadSuccess
          onUploadMore={() => setStep(STEPS.PHOTO_UPLOAD)}
          onRequestClose={onRequestClose}
          albumId={albumId}
        />
      )}
    </div>
  );
};

AlbumSlideshowOnboarding.propTypes = propTypes;

export default AlbumSlideshowOnboarding;
